import { useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import type { DemandBreakdownModeRow } from '@ebalance/proto/bffuser/v1/demand_breakdown_mode_model_pb'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { isNetworkError } from '~/libs/isNetworkError'
import { mapErrorToMessage } from '~/libs/mapConnectError'
import { isRetriableDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'
import { useDeleteDemandBreakdownMode } from '~/pages/DemandSettings/hooks/useDemandBreakdownModeMutations'
import { breakdownRetailerLabel } from '~/pages/DemandSettings/models/breakdownModeRows'

const DELETE_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: 'page:demandSettings.breakdownMode.serverError.aborted',
}

type Props = {
  /** 開いた時点の行。楽観ロックの基準 (`updatedAt`) もこの行のものを送る。 */
  row: DemandBreakdownModeRow
  onClose: () => void
}

/** 想定モードの設定の削除確認 (AC-4 / AC-7 / AC-8)。削除すると一括想定 (既定) に戻る。 */
export const DeleteBreakdownModeDialog = ({
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteDemandBreakdownMode()
  const [error, setError] = useState<
    { message: string; retriable: boolean } | undefined
  >(undefined)

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        areaCode: row.areaCode,
        retailerCode: row.retailerCode,
        expectedUpdatedAt: row.updatedAt,
      })
      onClose()
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      setError({
        message:
          err instanceof ConnectError && isNetworkError(err)
            ? t('common:connectError.deleteFailed')
            : mapErrorToMessage(err, t, DELETE_ERROR_KEYS),
        retriable: isRetriableDeleteError(err),
      })
    }
  }

  // 再試行しても同じ結果になる失敗 (他の利用者が先に変更した 等) は、削除ボタンを消して閉じるだけにする
  const isNonRetriableError = error !== undefined && !error.retriable

  return (
    <ConfirmDialog
      body={
        error?.message ??
        t('page:demandSettings.breakdownMode.deleteBody', {
          retailer: breakdownRetailerLabel(row),
        })
      }
      cancelLabel={isNonRetriableError ? t('common:action.close') : undefined}
      confirmLabel={t('common:action.delete')}
      destructive
      hideConfirm={isNonRetriableError}
      isLoading={isPending}
      isOpen
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t('page:demandSettings.breakdownMode.deleteTitle')}
    />
  )
}
